/**
 * Credentialed fetch utility.
 * Adds credentials: 'include' only for *.vaisala.com URLs,
 * and applies a configurable timeout via AbortSignal (default 10s).
 */

/**
 * Returns true if the given hostname is exactly "vaisala.com"
 * or ends with ".vaisala.com".
 */
function isVaisalaDomain(hostname: string): boolean {
  const lower = hostname.toLowerCase();
  return lower === 'vaisala.com' || lower.endsWith('.vaisala.com');
}

/**
 * Creates an AbortSignal that aborts when either the timeout elapses
 * or the caller's signal fires — whichever comes first.
 *
 * Uses AbortSignal.any() where available; falls back to a manual
 * AbortController for older browsers (Safari <17.4, Chrome <116).
 */
function composeSignals(timeoutMs: number, callerSignal?: AbortSignal | null): AbortSignal {
  const timeoutSignal = AbortSignal.timeout(timeoutMs);

  if (!callerSignal) {
    return timeoutSignal;
  }

  // Use AbortSignal.any() if available (Chrome 116+, Safari 17.4+, Firefox 124+)
  if ('any' in AbortSignal) {
    return AbortSignal.any([timeoutSignal, callerSignal]);
  }

  // Fallback: manual composition via a new AbortController
  const controller = new AbortController();

  const onAbort = () => controller.abort();

  if (callerSignal.aborted) {
    controller.abort();
    return controller.signal;
  }

  callerSignal.addEventListener('abort', onAbort, { once: true });
  timeoutSignal.addEventListener('abort', onAbort, { once: true });

  return controller.signal;
}

/**
 * A fetch wrapper that:
 * - Sets `credentials: 'include'` for URLs on *.vaisala.com
 * - Applies an AbortSignal timeout (default 10s, configurable via `timeoutMs`)
 * - Composes the caller's AbortSignal (if provided) with the timeout signal
 *   via AbortSignal.any() (requires Chrome 116+, Safari 17.4+, Firefox 124+)
 */
export interface CredentialedFetchOptions extends RequestInit {
  /** Timeout in milliseconds. Defaults to 10 000 (10s). */
  timeoutMs?: number;
}

export async function credentialedFetch(
  url: string,
  options?: CredentialedFetchOptions,
): Promise<Response> {
  const parsed = new URL(url);
  const includeCredentials = isVaisalaDomain(parsed.hostname);

  const timeoutMs = options?.timeoutMs ?? 10_000;
  const signal = composeSignals(timeoutMs, options?.signal);

  // Exclude timeoutMs from the options passed to fetch
  const { timeoutMs: _ignored, ...restOptions } = options ?? {};

  const fetchOptions: RequestInit = {
    ...restOptions,
    signal,
    ...(includeCredentials ? { credentials: 'include' } : {}),
  };

  return fetch(url, fetchOptions);
}
